import axios from "axios";
import { API_ORIGIN } from "../config/api.base";
import { emit } from "../lib/walletEvents";

const api = axios.create({
  baseURL: API_ORIGIN,

  withCredentials: true,
  // 2026-06-25 — bumped from 15s to 30s. Heavy admin / analyzer
  // endpoints (per-tournament chart loads scanning thousands of
  // RawBall rows) occasionally cross 15s under load even on the
  // resized box, surfacing "timeout exceeded 15000ms" toasts to
  // operators. 30s covers the 99th percentile cleanly. Long-running
  // chat / multipart / process paths still get their explicit
  // longer overrides below.
  timeout: 30000,
});

// Django APPEND_SLASH redirects unslashed URLs with a 301. Browsers refuse to
// follow that redirect on credentialed requests (nginx's redirect response has
// no CORS headers), so the call silently fails and any dropdown-fetch breaks.
// This interceptor adds the slash on the way out so we never hit the redirect.
//
// Same hook also injects a per-request UUID into X-Idempotency-Key on POST/
// PUT/DELETE calls to credit-gated endpoints. The backend's CreditGatedMixin
// uses this for replay protection on retries (e.g. user double-clicks a
// "Generate report" button). UUIDs are scoped to ONE request — repeating
// the same logical action gets a fresh key, so a true retry that you
// WANT to dedup must be sent with the same key from the call site.
const _CREDIT_GATED_PATTERNS = [
  /\/api\/matches\/[^/]+\/ai-report\/?$/,
  /\/api\/matches\/[^/]+\/ai-tag-balls\/?/,
  /\/api\/matches\/[^/]+\/press-report\.pdf\/?$/,
  /\/api\/matches\/ai-coding\/suggest\/?$/,
  /\/api\/scoring\/balls\/[^/]+\/commentary\/regenerate\/?$/,
  /\/api\/scoring\/ai\/scorer-chat\/?$/,
];

// LLM-backed chat endpoints. Anthropic + tool-use round-trips can take
// 30-60s on complex questions (multi-step tool calls, large schema
// payloads). The default 15s axios timeout kills the request mid-flight
// and the SPA shows "time exceeded - 15000ms". Bump to 2 minutes — well
// under any sane gateway timeout but long enough for real tool-use.
const _LONG_RUNNING_PATTERNS = [
  /\/api\/admin\/v2\/schema\/chat\/?$/,
  /\/api\/scoring\/ai\/scorer-chat\/?$/,
];

const _LONG_RUNNING_TIMEOUT_MS = 120 * 1000;

function _isCreditGated(url) {
  if (!url) return false;
  return _CREDIT_GATED_PATTERNS.some((re) => re.test(url));
}

function _isLongRunning(url) {
  if (!url) return false;
  return _LONG_RUNNING_PATTERNS.some((re) => re.test(url));
}

function _uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "fp-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

api.interceptors.request.use((config) => {
  const raw = config.url || "";
  // Leave well alone: absolute URLs to other origins, data URIs, anything with a
  // file extension on the last path segment (e.g. .json, .pdf, .webmanifest).
  const [pathPart, queryPart = ""] = raw.split("?");
  const lastSeg = pathPart.split("/").pop() || "";
  const hasExt = /\.[a-z0-9]{2,5}$/i.test(lastSeg);
  if (pathPart && !pathPart.endsWith("/") && !hasExt) {
    config.url = pathPart + "/" + (queryPart ? "?" + queryPart : "");
  }

  // X-Idempotency-Key on credit-gated mutating calls
  const method = (config.method || "get").toLowerCase();
  if (method !== "get" && method !== "head" && _isCreditGated(config.url)) {
    config.headers = config.headers || {};
    if (!config.headers["X-Idempotency-Key"]) {
      config.headers["X-Idempotency-Key"] = _uuid();
    }
  }

  // Multipart uploads (video ZIPs, bulk player CSV, scorecard PDFs) routinely
  // take longer than 15s — a 1 GB ZIP at 5 MB/s is 3+ minutes just streaming.
  // Bump the per-request timeout to 30 minutes when the call carries multipart
  // form data OR a FormData body. Caller can still pass an explicit `timeout`
  // value to override.
  if (config.timeout === 15000 || config.timeout === 30000 || config.timeout == null) {
    const ct = (config.headers && (
      config.headers["Content-Type"] ||
      config.headers["content-type"] ||
      ""
    )) || "";
    const isFormData = typeof FormData !== "undefined" && config.data instanceof FormData;
    if ((typeof ct === "string" && ct.includes("multipart")) || isFormData) {
      config.timeout = 30 * 60 * 1000;
    }
  }

  // Chat endpoints back onto Claude with tool-use — bump to 2 minutes
  // so the SPA doesn't kill the request before the model finishes a
  // multi-step answer. Caller-supplied `timeout` always wins.
  if ((config.timeout === 15000 || config.timeout === 30000 || config.timeout == null) && _isLongRunning(config.url)) {
    config.timeout = _LONG_RUNNING_TIMEOUT_MS;
  }

  return config;
});

// 402 Payment Required: backend signals one of three structured failures.
// We emit on the wallet event bus so global modals can render the right
// CTA (top up / reactivate / contact for storage). The original error
// still rejects so call sites can show in-place feedback if they want.
//
// 423 Locked: wallet locked for fraud review.
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 402 && data && typeof data === "object") {
      const code = data.error || data.code;
      if (code === "insufficient_credits") {
        emit("insufficient_credits", data);
      } else if (code === "subscription_lapsed") {
        emit("subscription_lapsed", data);
      } else if (code === "storage_cap_exceeded") {
        emit("storage_cap_exceeded", data);
      }
    } else if (status === 423 && data && typeof data === "object") {
      if ((data.error || data.code) === "wallet_locked") {
        emit("wallet_locked", data);
      }
    } else if (status === 403 && data && typeof data === "object") {
      // Email-verification gate on wallet debits — user has credits but
      // hasn't clicked the verify link yet. SPA shows a modal with a
      // resend-verification CTA. See backend wallet/exceptions.py.
      if ((data.error || data.code) === "email_not_verified") {
        emit("email_not_verified", data);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
